// El número compuesto con el que Business Central identifica una apertura y un
// cierre de caja, leído en un solo sitio.
//
// No es un número: es una clave concatenada con punto y coma, y con **dos
// formatos distintos** que hay que no confundir:
//
// - Apertura: `TPV;Caja;Fecha`          → `"MALL DEL SOL;PVMDSC3;07/08/26"`
// - Cierre:   `Tienda;TPV;Fecha;Hora`   → `"MALL DEL SOL;PVMDSC3;07/08/26;17:15"`
//
// **Corrección del 19/10/2026.** Aquí decía que la entidad de aperturas no trae
// un campo de tienda. No es así: `AperturaDeCaja` (página 90110) publica
// `Tienda`, `Caja` y `FechaDeApertura` desde su primera versión --leído en el
// fuente, y visto en PR DEV el 18/10/2026--. Lo que de verdad sólo viaja dentro
// del número es lo del **cierre**: `CierreDeCaja` (90111) no publica ni la
// tienda, ni la caja, ni la hora --no existe `HoraDeCierre`--. La tienda y la
// caja se pueden sacar de su apertura por `idApertura`, como ya hace
// `AperturaDeCajaForm.js`; la hora, no. Quien lea una apertura puede usar sus
// campos en vez de partir el número.
//
// Estaba partido a mano en cinco sitios, cada uno con su índice escrito
// suelto. Un índice equivocado no falla: devuelve el segmento de al lado, que
// también es una cadena con pinta de válida --la tienda por la caja, por
// ejemplo-- y nadie se entera hasta que algo no casa. Acá está una vez, con el
// formato escrito al lado.
//
// **Esto no arregla el fondo**, que es depender de una clave concatenada: eso
// necesita que Business Central publique los campos por separado. Lo que hace es
// dejar un solo sitio donde cambiarlo el día que los publique.

const segmentos = (no) =>
  String(no ?? "")
    .split(";")
    .map((parte) => parte.trim());

/**
 * La tienda, que es el primer segmento en los dos formatos.
 *
 * @param {string} no
 * @returns {string|null}
 */
export const tiendaDelNumero = (no) => segmentos(no)[0] || null;

/**
 * La caja, que es el segundo segmento en los dos formatos.
 *
 * @param {string} no
 * @returns {string|null}
 */
export const cajaDelNumero = (no) => segmentos(no)[1] || null;

/**
 * La fecha, que es el tercer segmento en los dos formatos, tal como la escribe
 * Business Central (`dd/MM/yy`). No se convierte: quien la usa la compara o la
 * reenvía con ese mismo formato.
 *
 * @param {string} no
 * @returns {string|null}
 */
export const fechaDelNumero = (no) => segmentos(no)[2] || null;

/**
 * La hora del cierre, que es el último segmento --y sólo la hay en el número de
 * un cierre--.
 *
 * Se comprueba que parezca una hora antes de devolverla: el número de una
 * apertura termina en la fecha, y darla por hora dejaría un cierre emparejado
 * con la apertura equivocada.
 *
 * @param {string} no
 * @returns {string|null} `HH:mm`, o `null` si el último segmento no es una hora.
 */
export const horaDelCierre = (no) => {
  const partes = segmentos(no);
  const ultimo = partes[partes.length - 1];
  return /^\d{1,2}:\d{2}$/.test(ultimo) ? ultimo : null;
};
